import { persistentReducedStream, reducer } from '@ardoq/rxbeach';
import { EMPTY, map, Observable } from 'rxjs';
import { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import {
  AffectedWorkspace,
  InstanceToTransferState,
  SuccessState,
  TransferDirection,
  TransferState,
} from 'integrations/common/streams/transferState/types';
import {
  transferFailure,
  startTransfer,
  transferSuccess,
  resetTransfer,
  setTransferDirection,
  updateTabularMapping,
  setInfoMessage,
  setFetchSelectionDataSuccess,
  updateSelectedWorkspaces,
  setViewSegment,
} from 'integrations/common/streams/transferState/actions';
import { Maybe } from '@ardoq/common-helpers';
import _ from 'lodash/fp';
import { tablesComplaints } from 'integrations/common/streams/transferState/utils';
import { withoutEmptyTableMappings } from 'integrations/common/streams/tabularMappings/utils';
import { buildInitialStreamState } from '../utils';
import { ActionCreatorParameter } from '../../utils/actionCreatorWithIntegrationId';

export const initialInstanceState: TransferState = {
  requestStatus: 'INIT',
  dryRun: true,
  async: true,
  transferDirection: TransferDirection.IMPORT,
  infoMessage: null,
  isSelectionDataFetched: false,
  viewSegment: 'summary',
};

const isCreatedWorkspace = (ws: AffectedWorkspace) => Boolean(ws.id);

const defaultState: InstanceToTransferState =
  buildInitialStreamState<TransferState>(() => initialInstanceState);

const transferSuccessReducer = (
  currentState: InstanceToTransferState,
  {
    integrationId,
    response,
    dryRun,
  }: ActionCreatorParameter<typeof transferSuccess>
) => {
  if (dryRun) {
    return {
      ...currentState,
      [integrationId]: {
        ...currentState[integrationId],
        requestStatus: 'SUCCESS',
        response,
        tablesComplaints: tablesComplaints(response.tables),
        selectedWorkspaces: [],
        ...(response.totals.validation.length > 0 && {
          viewSegment: 'worksheets',
        }),
      },
    };
  }

  return _.update(
    integrationId,
    (dryRunState: SuccessState) => {
      const mergedResult = _.merge(dryRunState, {
        requestStatus: 'SUCCESS',
        tablesComplaints: tablesComplaints(response.tables),
        response: dryRunState.response,
      });

      // Populating affectedWorkspaces, selectedWorkspaces outside of lodash/fp merge
      // to avoid duplicating values with _.merge fn

      const affectedWorkspaces = _.unionBy(
        'id',
        dryRunState.response.affectedWorkspaces.filter(isCreatedWorkspace),
        response.affectedWorkspaces.filter(isCreatedWorkspace)
      );

      return {
        ...mergedResult,
        response: {
          ...mergedResult.response,
          affectedWorkspaces,
        },
        selectedWorkspaces: affectedWorkspaces.map(w => w.id),
      };
    },
    currentState
  );
};
export const handleTransferSuccess = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof transferSuccess>
>(transferSuccess, transferSuccessReducer);

const updateTabularMappingReducer = (
  currentState: InstanceToTransferState,
  {
    integrationId,
    tabularMapping,
  }: ActionCreatorParameter<typeof updateTabularMapping>
) => {
  return _.update(
    integrationId,
    s => ({
      ...s,
      tabularMapping,
    }),
    currentState
  );
};
const handleUpdateTabularMapping = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof updateTabularMapping>
>(updateTabularMapping, updateTabularMappingReducer);

const setTransferDirectionReducer = (
  currentState: InstanceToTransferState,
  {
    transferDirection,
    integrationId,
  }: ActionCreatorParameter<typeof setTransferDirection>
) => {
  return _.update(
    integrationId,
    s => ({
      ..._.merge(s, {
        transferDirection,
      }),
    }),
    currentState
  );
};
const handleSetTransferDirection = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof setTransferDirection>
>(setTransferDirection, setTransferDirectionReducer);

const startTransferReducer = (
  currentState: InstanceToTransferState,
  {
    integrationId,
    tabularMapping,
    dryRun,
    async,
    isSelectionDataFetched = false,
  }: ActionCreatorParameter<typeof startTransfer>
) =>
  _.update(
    integrationId,
    s => ({
      requestStatus: 'LOADING',
      dryRun,
      async,
      tabularMapping: withoutEmptyTableMappings(tabularMapping),
      // TODO: https://ardoqcom.atlassian.net/browse/ARD-19393
      // keeping the previous response
      response: s.response,
      transferDirection: s.transferDirection,
      infoMessage: null,
      isSelectionDataFetched,
      selectedWorkspaces: s.selectedWorkspaces,
      viewSegment: s.viewSegment,
    }),
    currentState
  );
export const handleStartTransfer = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof startTransfer>
>(startTransfer, startTransferReducer);

const transferFailureReducer = (
  currentState: InstanceToTransferState,
  {
    integrationId,
    errorMessage,
    errorData,
  }: ActionCreatorParameter<typeof transferFailure>
) =>
  _.update(
    integrationId,
    s =>
      _.merge(s, {
        requestStatus: 'FAILURE',
        errorMessage,
        errorData,
        viewSegment: 'worksheets',
      }),
    currentState
  );
export const handleTransferFailure = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof transferFailure>
>(transferFailure, transferFailureReducer);

const resetTransferReducer = (
  currentState: InstanceToTransferState,
  {
    integrationId,
    resetTransferDirection = true,
  }: ActionCreatorParameter<typeof resetTransfer>
): InstanceToTransferState => ({
  ...currentState,
  [integrationId]: {
    ...initialInstanceState,
    ...(!resetTransferDirection && {
      transferDirection: currentState[integrationId].transferDirection,
      isSelectionDataFetched:
        currentState[integrationId].isSelectionDataFetched,
    }),
  },
});

export const handleResetTransfer = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof resetTransfer>
>(resetTransfer, resetTransferReducer);

const setInfoMessageReducer = (
  currentState: InstanceToTransferState,
  { integrationId, infoMessage }: ActionCreatorParameter<typeof setInfoMessage>
): InstanceToTransferState =>
  _.update(
    integrationId,
    s => ({
      ..._.merge(s, {
        infoMessage,
      }),
    }),
    currentState
  );

const handleSetInfoMessage = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof setInfoMessage>
>(setInfoMessage, setInfoMessageReducer);

const setFetchSelectionDataSuccessReducer = (
  currentState: InstanceToTransferState,
  { integrationId }: ActionCreatorParameter<typeof setFetchSelectionDataSuccess>
): InstanceToTransferState =>
  _.update(
    integrationId,
    s => ({
      ..._.merge(s, {
        isSelectionDataFetched: true,
      }),
    }),
    currentState
  );

const handleSetFetchSelectionDataSuccess = reducer<
  InstanceToTransferState,
  ActionCreatorParameter<typeof setFetchSelectionDataSuccess>
>(setFetchSelectionDataSuccess, setFetchSelectionDataSuccessReducer);
const updateSelectedWorkspacesReducer = (
  currentState: InstanceToTransferState,
  {
    integrationId,
    selectedWorkspaces,
  }: ActionCreatorParameter<typeof updateSelectedWorkspaces>
) => ({
  ...currentState,
  [integrationId]: {
    ...currentState[integrationId],
    selectedWorkspaces: selectedWorkspaces,
  },
});

const setViewSegmentReducer = (
  currentState: InstanceToTransferState,
  { integrationId, viewSegment }: ActionCreatorParameter<typeof setViewSegment>
) => ({
  ...currentState,
  [integrationId]: {
    ...currentState[integrationId],
    viewSegment,
  },
});

export const transferState$ = persistentReducedStream(
  `transferState$`,
  defaultState,
  [
    handleTransferSuccess,
    handleUpdateTabularMapping,
    handleStartTransfer,
    handleTransferFailure,
    handleResetTransfer,
    handleSetTransferDirection,
    handleSetInfoMessage,
    handleSetFetchSelectionDataSuccess,
    reducer(updateSelectedWorkspaces, updateSelectedWorkspacesReducer),
    reducer(setViewSegment, setViewSegmentReducer),
  ]
);

const integrationToTransferStateStream = new Map<
  IntegrationId,
  Observable<TransferState>
>();

export const getTransferStateStream = (integrationId: Maybe<IntegrationId>) => {
  if (!integrationId) {
    return EMPTY;
  }

  const transferStateStream =
    integrationToTransferStateStream.get(integrationId);

  if (transferStateStream) {
    return transferStateStream;
  }

  const stream$ = transferState$.pipe(
    map(transferState => transferState[integrationId])
  );

  integrationToTransferStateStream.set(integrationId, stream$);

  return stream$;
};
